import { useState, useEffect, useCallback } from 'react';
import { 
  Clock, 
  User, 
  Activity, 
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { 
  getActivityLogs
} from '../../api/activityLogs';
import styles from './ActivityLog.module.scss';

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    action: ''
  });
  
  const itemsPerPage = 10;

  const loadActivityLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        offset: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await getActivityLogs(params);
      setLogs(response.logs || []);
      setTotalPages(Math.ceil((response.total || 0) / itemsPerPage));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  useEffect(() => {
    loadActivityLogs();
  }, [loadActivityLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      action: ''
    });
    setCurrentPage(1);
  };


  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getActionIcon = (action) => {
    switch (action.toLowerCase()) {
      case 'create':
      case 'add':
      case 'update':
      case 'edit':
      case 'delete':
      case 'remove':
      case 'purchase':
      case 'formulation_use':
      default:
        return <Activity className={styles.actionIcon} />;
    }
  };

  const getActionColor = (action) => {
    switch (action.toLowerCase()) {
      case 'create':
      case 'add':
      case 'user_created':
      case 'user_auto_created':
      case 'assignment_created':
      case 'formulation_created':
      case 'product_created':
      case 'purchase_created':
      case 'stock_movement_created':
        return styles.createAction;
      case 'update':
      case 'edit':
      case 'modify_user':
      case 'formulation_updated':
      case 'product_updated':
        return styles.updateAction;
      case 'delete':
      case 'remove':
      case 'delete_user':
      case 'formulation_deleted':
      case 'product_deleted':
        return styles.deleteAction;
      case 'stock_management':
      case 'manual stock update':
        return styles.stockAction;
      case 'formulation_use':
      case 'formulation usage':
      case 'manufacturing_production':
        return styles.formulationAction;
      case 'user_online':
      case 'login':
        return styles.loginAction;
      case 'user_offline':
      case 'logout':
        return styles.logoutAction;
      case 'approve_user':
      case 'extension_approved':
        return styles.approveAction;
      case 'assignment_otp_verified':
      case 'extension_otp_verified':
      case 'otp_sent':
      case 'admin_otp_sent':
      case 'admin_otp_verified':
        return styles.otpAction;
      case 'assignment_completed':
        return styles.completedAction;
      case 'extension_requested':
      case 'extension_rejected':
        return styles.extensionAction;
      case 'user_registered':
        return styles.registrationAction;
      case 'uid_auto_fixed':
        return styles.systemAction;
      case 'update_log_note':
        return styles.noteAction;
      default:
        return styles.defaultAction;
    }
  };

  return (
    <div className={styles.activityLog}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <Activity className={styles.titleIcon} />
          <div>
            <h1>Activity Log</h1>
            <p>Track all system activities and user actions</p>
            {logs.length > 0 && (
              <div className={styles.activityCount}>
                Showing {logs.length} activities (Page {currentPage} of {totalPages})
              </div>
            )}
          </div>
        </div>
        
        <button
          className={styles.refreshButton}
          onClick={loadActivityLogs}
          disabled={loading}
        >
          <RefreshCw size={16} className={loading ? styles.spinning : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className={styles.error}>
          <Activity size={16} />
          {error}
        </div>
      )}

       <div className={styles.filters}>
         <div className={styles.filterGroup}>
           <label>Action:</label>
           <select
             value={filters.action}
             onChange={(e) => handleFilterChange('action', e.target.value)}
             className={styles.filterSelect}
           >
             <option value="">All Actions</option>
             
             {/* User Management */}
             <optgroup label="User Management">
               <option value="login">Login</option>
               <option value="logout">Logout</option>
               <option value="user_online">User Online</option>
               <option value="user_offline">User Offline</option>
               <option value="user_created">User Created</option>
               <option value="user_auto_created">User Auto Created</option>
               <option value="user_registered">User Registered</option>
               <option value="approve_user">Approve User</option>
               <option value="modify_user">Modify User</option>
               <option value="delete_user">Delete User</option>
               <option value="uid_auto_fixed">UID Auto Fixed</option>
             </optgroup>
             
             {/* Assignment Management */}
             <optgroup label="Assignment Management">
               <option value="ASSIGNMENT_CREATED">Assignment Created</option>
               <option value="ASSIGNMENT_OTP_VERIFIED">Assignment OTP Verified</option>
               <option value="ASSIGNMENT_COMPLETED">Assignment Completed</option>
               <option value="EXTENSION_REQUESTED">Extension Requested</option>
               <option value="EXTENSION_OTP_VERIFIED">Extension OTP Verified</option>
               <option value="EXTENSION_APPROVED">Extension Approved</option>
               <option value="EXTENSION_REJECTED">Extension Rejected</option>
             </optgroup>
             
             {/* Product & Formulation Management */}
             <optgroup label="Product & Formulation">
               <option value="PRODUCT_CREATED">Product Created</option>
               <option value="PRODUCT_UPDATED">Product Updated</option>
               <option value="PRODUCT_DELETED">Product Deleted</option>
               <option value="FORMULATION_CREATED">Formulation Created</option>
               <option value="FORMULATION_UPDATED">Formulation Updated</option>
               <option value="FORMULATION_DELETED">Formulation Deleted</option>
               <option value="formulation_use">Formulation Usage</option>
             </optgroup>
             
             {/* Stock & Purchase Management */}
             <optgroup label="Stock & Purchase">
               <option value="STOCK_MANAGEMENT">Stock Management</option>
               <option value="Manual Stock Update">Manual Stock Update</option>
               <option value="STOCK_MOVEMENT_CREATED">Stock Movement Created</option>
               <option value="PURCHASE">Purchase</option>
               <option value="PURCHASE_CREATED">Purchase Created</option>
               <option value="MANUFACTURING_PRODUCTION">Manufacturing Production</option>
               <option value="Manufacturing Consumption">Manufacturing Consumption</option>
               <option value="Manufacturing Production">Manufacturing Production</option>
               <option value="MANUFACTURING">Manufacturing</option>
             </optgroup>
             
             {/* OTP & Security */}
             <optgroup label="OTP & Security">
               <option value="otp_sent">OTP Sent</option>
               <option value="admin_otp_sent">Admin OTP Sent</option>
               <option value="admin_otp_verified">Admin OTP Verified</option>
             </optgroup>
             
             {/* System & Other */}
             <optgroup label="System & Other">
               <option value="update_log_note">Update Log Note</option>
               <option value="CREATE">Create (Generic)</option>
               <option value="UPDATE">Update (Generic)</option>
               <option value="DELETE">Delete (Generic)</option>
             </optgroup>
           </select>
         </div>

         <button
           className={styles.clearFiltersButton}
           onClick={clearFilters}
         >
           Clear Filters
         </button>
       </div>


      <div className={styles.logsList}>
        {loading ? (
          <div className={styles.loading}>
            <RefreshCw className={styles.spinning} />
            <span>Loading activity logs...</span>
          </div>
        ) : logs.length === 0 ? (
          <div className={styles.empty}>
            <Activity size={48} />
            <h3>No activity logs found</h3>
            <p>Try adjusting your filters or check back later for new activities.</p>
          </div>
        ) : (
          logs.map((log) => (
            <div key={log.id} className={styles.logRow}>
              <div className={styles.logLeft}>
                <div className={`${styles.actionBadge} ${getActionColor(log.action)}`}>
                  {getActionIcon(log.action)}
                  <span>{log.action}</span>
                </div>
                <div className={styles.description}>{log.description}</div>
              </div>
              
              <div className={styles.logRight}>
                <div className={styles.userInfo}>
                  <User size={12} />
                  <span>
                    {log.user_info ? `${log.user_info.first_name} ${log.user_info.last_name}` : 'System'}
                  </span>
                </div>
                <div className={styles.timestamp}>
                  <Clock size={12} />
                  {formatTimestamp(log.timestamp)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.paginationButton}
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={16} />
            First
          </button>
          
          <button
            className={styles.paginationButton}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={16} />
            Previous
          </button>
          
          <div className={styles.pageInfo}>
            Page {currentPage} of {totalPages}
            <span className={styles.totalItems}>
              ({logs.length} items on this page)
            </span>
          </div>
          
          <button
            className={styles.paginationButton}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
            <ChevronRight size={16} />
          </button>
          
          <button
            className={styles.paginationButton}
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            Last
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
